/*
 * Copyright (c) 2025.
 * This code and its derivatives belong to BETICA LTD and may not be copied, reproduced, amended or used in any way without the permission of BETICA LTD
 * @author torrejonv
 *
 */

/**
 * Main entry point for the application
 * Sets up all required providers and renders the root component
 * @module index
 */

import React from "react";
import { createRoot } from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";
import { Provider } from "react-redux";
import { BrowserRouter } from "react-router-dom";
import { BreakpointProvider } from "react-socks";
import { PersistGate } from "redux-persist/integration/react";

import App from "./App";
import store, { persistor } from "./redux/store";
import reportWebVitals from "./reportWebVitals";
import ConfigSettingsProvider from "./settings/ConfigSettings";

import ErrorBoundaryPage from "components/ErrorBoundaryPage/components";

// Import font styles
import "./assets/scss/fonts.module.scss";

// Import i18n configuration
// This ensures that the i18next instance is bundled into our app and initialized
import "./services/i18n";
import { WebSocketProvider } from "./websockets/WebSocket";

/**
 * DOM element that serves as the root container for our React app
 * @type {HTMLElement}
 */
const container = document.getElementById("root");

/**
 * React 18 createRoot instance for rendering the application
 * @type {import('react-dom/client').Root}
 */
const root = createRoot(container);

/**
 * Render the application with all required providers
 * Provider hierarchy:
 * - StrictMode: Enables additional development checks
 * - Suspense: Handles async loading states at the root level
 * - Redux Provider: Makes Redux store available to all components
 * - PersistGate: Delays UI rendering until persisted state is retrieved
 * - ConfigSettingsProvider: Provides configuration settings
 * - WebSocketProvider: Manages WebSocket connections
 * - HelmetProvider: Manages document head content
 * - BrowserRouter: Provides routing capabilities
 * - BreakpointProvider: Handles responsive breakpoints
 * - ErrorBoundaryPage: Catches and handles component errors
 */
root.render(
  <React.StrictMode>
    <React.Suspense fallback="">
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <ConfigSettingsProvider>
            <WebSocketProvider>
              <HelmetProvider>
                <BrowserRouter>
                  <BreakpointProvider>
                    <ErrorBoundaryPage
                      onActionTap={() => {
                        persistor.purge().then(() => {
                          console.warn("A persistor is successfully purged.");
                        });
                        window.location.href = "/";
                      }}
                    >
                      <App />
                    </ErrorBoundaryPage>
                  </BreakpointProvider>
                </BrowserRouter>
              </HelmetProvider>
            </WebSocketProvider>
          </ConfigSettingsProvider>
        </PersistGate>
      </Provider>
    </React.Suspense>
  </React.StrictMode>,
);

/**
 * Initialize web vitals reporting
 * Can be configured to log results or send to an analytics endpoint
 * @see https://bit.ly/CRA-vitals for more information on web vitals measurement
 */
reportWebVitals();
